// dossierEditHandlers — handlers d'édition Client / Logement de la fiche dossier.
// CC-DEV1-18 — extraits d'AdminBlock (devenu code mort depuis la refonte P1.1)
// pour en faire la SOURCE UNIQUE réutilisable (InfosTab + tout futur écran).
// Chaque handler fait son PATCH et renvoie { ok, error } — le composant garde
// la main sur le toast / le refresh (onUpdate).
import { API, getAuthHeaders } from "./helpers";

// PATCH /metier/clients/{id} — champs bénéficiaire éditables (mêmes champs
// qu'AdminBlock : nom/prénom/email/téléphone/occupation + contact secondaire).
// La whitelist backend (core/database.update_client.allowed_fields) couvre tous
// ces champs.
export async function patchClient(clientId, form, tenantId) {
  if (!clientId) return { ok: false, error: "Pas de client_id" };
  const headers = getAuthHeaders(tenantId, "application/json");
  const body = {
    civilite: form.civilite || null,
    nom: form.nom,
    prenom: form.prenom,
    email: form.email,
    telephone: form.telephone,
    statut_occupation: form.statut_occupation || null,
    // 🔑 N°730 — le TYPE DE CLIENT devient saisissable. `core/client_type.py` portait
    // déjà la normalisation B2B/B2C (et `update_client` l'allowlist) : ⛔ rien de neuf
    // côté modèle, on BRANCHE ce qui existait sans écrivain d'écran.
    type_client: form.type_client || null,
    contact_secondaire_nom: form.contact_secondaire_nom || null,
    contact_secondaire_prenom: form.contact_secondaire_prenom || null,
    contact_secondaire_telephone: form.contact_secondaire_telephone || null,
    contact_secondaire_relation: form.contact_secondaire_relation || null,
  };
  // 🔑 N°1002 #8 — champs OPTIONNELS, envoyés SEULEMENT si l'écran les porte : une
  // personne morale se nomme par sa `raison_sociale`, et l'adresse est celle du client
  // canonique. Un appelant qui ne les fournit pas (InfosTab) ⛔ ne les efface PAS.
  // (Les deux sont dans l'allowlist `core/database.update_client.allowed_fields`.)
  for (const cle of ["raison_sociale", "adresse"]) {
    if (Object.prototype.hasOwnProperty.call(form, cle)) body[cle] = form[cle];
  }
  try {
    const res = await fetch(`${API}/api/metier/clients/${clientId}`, {
      method: "PATCH", headers, credentials: "include", body: JSON.stringify(body),
    });
    if (res.ok) return { ok: true };
    const err = await res.json().catch(() => ({}));
    return { ok: false, error: err.detail || "Erreur sauvegarde" };
  } catch {
    return { ok: false, error: "Erreur réseau" };
  }
}

// 🔴 N°1002 #8 — formulaire client PRÉREMPLI avec TOUS les champs que `patchClient`
// envoie. ⚠️ `patchClient` écrit `champ || null` : un champ absent du formulaire serait
// EFFACÉ sur le client canonique (type, occupation, contact secondaire…). On part donc
// TOUJOURS de la fiche existante, ⛔ jamais d'un formulaire partiel.
export function formulaireClient(client, dossier) {
  const c = client || {};
  const a = c.adresse && typeof c.adresse === "object" ? c.adresse : {};
  return {
    civilite: c.civilite || "",
    nom: c.nom || dossier?.client_nom || "",
    prenom: c.prenom || "",
    raison_sociale: c.raison_sociale || "",
    telephone: c.telephone || dossier?.telephone || "",
    email: c.email || "",
    type_client: c.type_client || "",
    statut_occupation: c.statut_occupation || "",
    contact_secondaire_nom: c.contact_secondaire_nom || "",
    contact_secondaire_prenom: c.contact_secondaire_prenom || "",
    contact_secondaire_telephone: c.contact_secondaire_telephone || "",
    contact_secondaire_relation: c.contact_secondaire_relation || "",
    adresse: {
      ...a,
      rue: a.rue || (typeof c.adresse === "string" ? c.adresse : "") || c.adresse_rue || "",
      code_postal: a.code_postal || c.code_postal || "",
      ville: a.ville || c.ville || "",
    },
  };
}

// PATCH /metier/dossiers/{id} — logement : adresse_projet (rue / CP / ville) +
// workflow_data.tef.infos_logement.type_logement. Le workflow_data existant est
// fusionné de façon NON destructive (comme AdminBlock).
export async function patchLogement(dossier, form, tenantId) {
  const dossierId = dossier?.id;
  if (!dossierId) return { ok: false, error: "Pas de dossier_id" };
  const headers = getAuthHeaders(tenantId, "application/json");
  const payload = {
    adresse_projet: {
      rue: form.rue,
      code_postal: form.code_postal,
      ville: form.ville,
    },
    workflow_data: {
      ...dossier.workflow_data,
      tef: {
        ...(dossier.workflow_data?.tef || {}),
        infos_logement: {
          ...(dossier.workflow_data?.tef?.infos_logement || {}),
          type_logement: form.type_logement,
        },
      },
    },
  };
  try {
    const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
      method: "PATCH", headers, credentials: "include", body: JSON.stringify(payload),
    });
    if (res.ok) return { ok: true };
    const err = await res.json().catch(() => ({}));
    return { ok: false, error: err.detail || "Erreur sauvegarde" };
  } catch {
    return { ok: false, error: "Erreur réseau" };
  }
}
